/* TODO LIST
- Loading state
*/

// coming_soon_date is a real UTC instant, so comparing it against the clock
// needs no conversion. Displaying it does: card.liquid renders the badge date
// with Liquid's `date` filter, which formats in the shop timezone, so the
// Algolia badge has to format there too or the two disagree for shoppers
// outside Pacific time.
const STORE_TIME_ZONE = 'America/Vancouver';

// Milliseconds to add to a store wall-clock reading to get UTC, at `instant`.
function storeTimeZoneOffset(instant) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: STORE_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(instant)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  const local = Date.UTC(
    +parts.year,
    parts.month - 1,
    +parts.day,
    +parts.hour % 24,
    +parts.minute,
    +parts.second
  );

  return instant - local;
}

/**
 * Use algoliaShopify for all product relateed data, including:
 * - Filters
 * - Sorting
 * - Pagination
 */
class ListingAlgolia extends HTMLElement {
  constructor() {
    super();

    // Component data
    this.loaded = false;
    this.indexAdjust = 0;
    this.collectionHandle = this.dataset.collectionHandle;
    this.collectionId = this.dataset.collectionId;
    this.sectionId = this.dataset.sectionId;
    this.searchTerms = this.dataset.searchTerms || '';
    this.queryId = '';

    this.priceResultsMin = null;
    this.priceResultsMax = null;

    setTimeout(() => {
      this.init();
    }, 10);
  }

  init() {
     const collectionPromoScript = document.getElementById('ColectionPromos');
    if (collectionPromoScript) {
      this.collectionPromos = JSON.parse(collectionPromoScript.textContent);
    }    

    // Algolia search client (vendored v5 lite: assets/algoliasearch-lite.umd.js)
    const lite = window['algoliasearch/lite'];
    if (!lite || typeof lite.liteClient !== 'function' || !window.algoliaShopify?.config) {
      console.warn('[Algolia] client or config unavailable');
      return;
    }

    this.searchClient = lite.liteClient(
      algoliaShopify.config.app_id,
      algoliaShopify.config.search_api_key,
      {
        baseHeaders: {
          'X-Algolia-UserToken': getAlgoliaUserToken(),
        },
      }
    );

    // Initial state
    const collectionQueryRuleValue = algoliaShopify.config
      .collection_id_query_rules
      ? this.collectionId
      : this.collectionHandle;

    this.analyticsTags = [
      collectionQueryRuleValue,
      'collection',
      getDeviceType(),
    ];

    this.hitsPerPage =
      algoliaShopify.config.products_full_results_hits_per_page;
    this.currentPage = 0;

    this.currentSortBy = '';
    this.currentFilters = {};

    this.sortData =
      algoliaShopify.config.collection_sort_orders &&
      algoliaShopify.config.collection_sort_orders[this.collectionId]
        ? algoliaShopify.config.collection_sort_orders[this.collectionId]
        : algoliaShopify.config.sort_orders;

    this.facetData =
      algoliaShopify.config.collection_facets &&
      algoliaShopify.config.collection_facets[this.collectionId]
        ? algoliaShopify.config.collection_facets[this.collectionId]
        : algoliaShopify.config.facets;
    // Remove all facets that are not enabled
    this.facetData = this.facetData.filter((facet) => facet.enabled === true);
    this.facetValuesSortOrder = {};

    this.facetFilters = [];

    this.facetData.forEach((facet) => {
      if (facet.enabled) {
        this.facetFilters.push(facet.name);
      }
    });

    this.setStatesFromURL();

    // DOM elements
    this.pillsContainer = document.querySelector('#AlgoliaFilterPills');
    this.itemCounts = document.querySelectorAll('.AlgoliaItemCount');
    this.sortBy = document.querySelector('#AlgoliaSortBy');
    this.sortByDrawer = document.querySelector('#AlgoliaDrawerSortBy');
    this.filtersDesktop = document.querySelector('#AlgoliaDesktopFilters');
    this.filtersDrawer = document.querySelector('#AlgoliaDrawerFilters');
    this.productList = document.querySelector('#AlgoliaProductGrid');
    this.paginationContainer = document.querySelector('#AlgoliaPagination');

    // Events
    this.debouncedOnSubmit = debounce((event) => {
      this.onFilterChange(event);
    }, 800);

    if (this.filtersDesktop) {
      this.filtersDesktop.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    if (this.sortBy) {
      this.generateSortFromConfig();
      this.sortBy.addEventListener('change', this.onSortChange.bind(this));
      this.sortByDrawer.addEventListener(
        'change',
        this.onSortChange.bind(this)
      );
    }

    if (this.paginationContainer) {
      //this.paginationContainer
      //  .querySelector('.pagination__select')
      //  .addEventListener('change', this.onPaginationSelect.bind(this));

      this.paginationContainer
        .querySelector('.pagination__button--prev')
        .addEventListener('click', this.onPaginationPrev.bind(this));

      this.paginationContainer
        .querySelector('.pagination__button--next')
        .addEventListener('click', this.onPaginationNext.bind(this));
    }

    window.addEventListener('popstate', (event) => {
      this.currentFilters = event.state ? event.state.searchParams : {};
      this.currentSortBy = event.state ? event.state.sortBy : '';
      this.currentPage = event.state ? event.state.page : 0;

      this.setDOMStates();
      this.search();
    });

    // Initial search. We require consent tracking to be loaded first.
    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      this.search.bind(this, true)
    );
  }

  disconnectedCallback() {
    if (this.onAlgoliaLoadedUnsubscriber) {
      this.onAlgoliaLoadedUnsubscriber();
    }

    if (this.onSwatchChangeUnsubscriber) {
      this.onSwatchChangeUnsubscriber();
    }
  }

  connectedCallback() {
    // If its a collection, send an Algolia Viewed Event
    this.onAlgoliaLoadedUnsubscriber = subscribe(
      PUB_SUB_EVENTS.algoliaLoaded,
      () => {
        if (this.collectionHandle) {
          window.Shopify.loadFeatures(
            [
              {
                name: 'consent-tracking-api',
                version: '0.1',
              },
            ],
            () => {
              aa('viewedFilters', {
                userToken: getAlgoliaUserToken(),
                index: this.buildIndexParams(),
                eventName: 'Landing Page Viewed',
                filters: [`collections:${this.collectionHandle}`],
              });
            }
          );
        }
      }
    );

    this.onSwatchChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.swatchChange,
      (target) => {
        aa('viewedObjectIDs', {
          userToken: getAlgoliaUserToken(),
          eventName: 'Product Viewed',
          index: this.buildIndexParams(),
          objectIDs: [String(target.dataset.variantId)],
        });
      }
    );
  }

  /**
   * Set the current parameters from the URL.
   * Only needs to do filters and sorting since those are the only
   * parameters that are not handled by the algoliaShopify API.
   */
  setStatesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.forEach((value, key) => {
      if (key == 'page') {
        this.currentPage = parseInt(value);
      } else if (key == 'sort_by') {
        this.currentSortBy = value;
      }
      else if (key == 'price') {
        this.currentFilters[key] = [parseInt(value.split(':')[0]), parseInt(value.split(':')[1])];
      } else if (this.facetFilters.includes(key)) {
        if (!this.currentFilters[key]) {
          this.currentFilters[key] = [];
        }

        this.currentFilters[key].push(value);
      }
    });
  }

  /**
   * Set the DOM states from the current parameters.
   */
  setDOMStates() {
    this.sortBy.value = this.currentSortBy;
    this.sortByDrawer.querySelectorAll('input').forEach((input) => {
      if (input.value == this.currentSortBy) {
        input.checked = true;
      } else {
        input.checked = false;
      }
    });

    Object.keys(this.currentFilters).forEach((filter) => {
      if (filter == 'price') {
        const priceMin = this.currentFilters[filter][0];
        const priceMax = this.currentFilters[filter][1];

        if (this.filtersDesktop) {
          const priceMinSlider = this.filtersDesktop.querySelector(`input[name="price_min_slider"]`);
          const priceFromInput = this.filtersDesktop.querySelector(`input[name="price_min"]`);

          const priceMaxSlider = this.filtersDesktop.querySelector(`input[name="price_max_slider"]`);
          const priceToInput = this.filtersDesktop.querySelector(`input[name="price_max"]`);

          if (priceMinSlider) {
            priceMinSlider.value = priceMin;
          }

          if (priceFromInput) {
            priceFromInput.value = priceMin;
          }

          if (priceMaxSlider) {
            priceMaxSlider.value = priceMax;
          }

          if (priceToInput) {
            priceToInput.value = priceMax;
          }
        }

        if (this.filtersDrawer) {
          const priceMinSlider = this.filtersDrawer.querySelector(`input[name="price_min_slider"]`);
          const priceFromInput = this.filtersDrawer.querySelector(`input[name="price_min"]`);

          const priceMaxSlider = this.filtersDrawer.querySelector(`input[name="price_max_slider"]`);
          const priceToInput = this.filtersDrawer.querySelector(`input[name="price_max"]`);

          if (priceMinSlider) {
            priceMinSlider.value = priceMin;
          }

          if (priceFromInput) {
            priceFromInput.value = priceMin;
          }

          if (priceMaxSlider) {
            priceMaxSlider.value = priceMax;
          }

          if (priceToInput) {
            priceToInput.value = priceMax;
          }
        }
      } else {
        this.currentFilters[filter].forEach((value) => {
          if (this.filtersDesktop) {
            const filterElement = this.filtersDesktop.querySelector(
              `input[name="${filter}"][value="${value}"]`
            );

            if (filterElement) {
              filterElement.checked = true;
            }
          }

          if (this.filtersDrawer) {
            const filterElement = this.filtersDrawer.querySelector(
              `input[name="${filter}"][value="${value}"]`
            );

            if (filterElement) {
              filterElement.checked = true;
            }
          }
        });
      }
    });
  }

  /**
   * Reset the product list.
   */
  resetProductList() {
    this.indexAdjust = 0;
    this.currentPage = 0;
    this.productList.innerHTML = '';
  }

  /**
   * Search algoliaShopify for the product data.
   * @param {boolean} fullBuild - Whether to fully rebuild the product list and filters. It'll only be false when a filter or sort is applied.
   */
  search(fullBuild = true, paginationTriggered = false) {
    this.cleanEmptyFilters();

    if (fullBuild && this.loaded) {
      this.resetProductList();
    }

    /**
     * This is used to build the search params for the algoliaShopify API.
     *
     * We need to send multiple queries in one request because in algolia, facet data is conjunctive.
     * Meaning, naturally, you can't apply multiple filters to the same facet.
     *
     * So, we need to build a query for each facet that is applied.
     *
     * For example, if you have a filter for color and a filter for size, you need to build two queries in addition to the default query.
     *
     * The first query will be the default query, which includes the color and size facetFilters.
     * The second query will be the query will only include the color facetFilters.
     * The third query will be the query will only include the size facetFilters.
     *
     * The first query will then be used to build the product list and the pagination, since this is the "true" query
     * that the customer is wanting to see.
     *
     * For all facets that are not applied (lets say: 'type'), the first query will ALSO be used. Since we want to
     * only show the filters that can actually be applied with the current filters.
     *
     * The second and third queries will be used to build their respective filters. The way it works is that
     * the facetFilters that are applied to the query include all filters except those in the same group.
     *
     * So for query 2, the facetFilters will be the color facetFilters, but not the size facetFilters.
     * Meaning that it will return all size filters, that can be applied with the current color filters.
     *
     * The same goes for query 3.
     *
     * IF there are no filters applied, the first query will be used to build ALL the filters.
     */

    const queryParams = this.buildMultipleSearchParams();

    let priceQueryPosition = 0;

    queryParams.forEach((query, queryIndex) => {
      if (query.numericFilters.length == 0) {
        priceQueryPosition = queryIndex;
      }
    });

    this.searchClient.search({ requests: queryParams }).then((queries) => {
      if (paginationTriggered) {
        this.productList.innerHTML = '';
      }

      const results = queries.results;

      if (results[priceQueryPosition].facets_stats?.price) {
        this.priceResultsMin = Math.floor(results[priceQueryPosition].facets_stats.price.min);
        this.priceResultsMax = Math.ceil(results[priceQueryPosition].facets_stats.price.max);
      }

      this.queryId = results[0].queryID;

      this.generateProductsFromHits(results[0].hits, results[0].queryID);
      this.generatePaginationFromResults(results[0]);
      this.generateCountsFromResults(results[0]);

      this.generatePillsFromFacets();

      if (fullBuild) {
        this.facetValuesSortOrder = {};

        // Sort this.facetFilters array to match the order of renderingContent.facetOrdering.facets.order
        // If a facet is not in the renderingContent.facetOrdering.facets.order, it should be at the end.
        if(results[0].renderingContent && results[0].renderingContent.facetOrdering) {
          this.facetValuesSortOrder = results[0].renderingContent.facetOrdering.values;

          let facetOrdering = results[0].renderingContent.facetOrdering.facets.order;          

          this.facetData.sort((a, b) => {
            const ai = facetOrdering.indexOf(a.name);
            const bi = facetOrdering.indexOf(b.name);
            return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
          });
        }

        if (results.length == 1) {
          this.generateFiltersFromFacets(results[0].facets);
        } else {
          // IF the facet is applied, use that facet's data to build the filters.
          // Otherwise, use the default facets.
          const facetLocation = {};

          Object.keys(this.currentFilters).forEach((facet, index) => {
            facetLocation[facet] = index + 1;
          });

          this.generateFiltersFromResults(results, facetLocation);
        }
      }

      if (paginationTriggered) {
        // Scroll to the top of the product list
        this.productList.scrollIntoView({ behavior: 'smooth' });
      }
    }).catch((error) => {
      console.warn('[Algolia] search failed', error);
    });

    this.loaded = true;
  }

  /**
   * Build the index params for the algoliaShopify API. The 'index' is the algolia product list to search.
   * @returns {string} The index params.
   */
  buildIndexParams() {
    if (this.currentSortBy != '') {
      return `${algoliaShopify.config.index_prefix}products${this.currentSortBy}`;
    } else {
      return `${algoliaShopify.config.index_prefix}products`;
    }
  }

  buildSearchQuery() {
    if (this.searchTerms) {
      return this.searchTerms;
    }

    return '';
  }

  /**
   * Build the search params for the algoliaShopify API.
   * @returns {object} The search params.
   */
  buildSearchParams() {
    const facetParams = this.buildFacetParams();

    const context = [];

    if (this.dataset.collectionHandle && facetParams.length == 0) {
      if (algoliaShopify.config.collection_id_query_rules) {
        context.push(`${this.dataset.collectionId}`);
      } else {
        context.push(`${this.dataset.collectionHandle}`);
      }
    }

    const params = {
      page: this.currentPage,
      hitsPerPage: this.hitsPerPage,

      // Context, controls wether rules should be applied
      ruleContexts: context,

      // Returns the facet data. This is used to build the filters DOM.
      facets: this.facetFilters,
      sortFacetValuesBy: 'alpha',

      // Filters to be used for the search
      filters: this.buildFilterParams(),
      facetFilters: facetParams, // By default, we want to just apply the filters that are applied to the DOM.
      numericFilters: this.buildNumericParams(),

      // List of product attributes to be returned
      attributesToRetrieve: ['*'],

      // Hard coded params
      distinct: true,
      clickAnalytics: true,
      enablePersonalization: false,
      userToken: getAlgoliaUserToken(),
      analyticsTags: this.analyticsTags,
    };

    return params;
  }

  /**
   * Build the multiple search params for the algoliaShopify API.
   * @returns {object} The multiple search params.
   */
  buildMultipleSearchParams() {
    const queries = [
      {
        indexName: this.buildIndexParams(),
        query: this.buildSearchQuery(),
        ...this.buildSearchParams(),
      },
    ];

    // Check if any filters are applied. If not, we can just use the default query.
    // Otherwise, we need to build a query for each facet that is applied.
    Object.keys(this.currentFilters).forEach((facet) => {
      const facetData = [];

      Object.keys(this.currentFilters).forEach((appliedFacet) => {
        if (appliedFacet == 'price') {
          return;
        }

        if (facet !== appliedFacet) {
          const facetGroup = [];

          this.currentFilters[appliedFacet].forEach((value) => {
            facetGroup.push(`${appliedFacet}:${value}`);
          });

          facetData.push(facetGroup);
        }
      });

      // Overrides must come after the spread, or the base params overwrite them.
      const query = {
        indexName: this.buildIndexParams(),
        query: '',
        ...this.buildSearchParams(),
        hitsPerPage: 1,
        facetFilters: facetData,
      };

      if (facet == 'price') {
        query.numericFilters = [];
      }

      queries.push(query);
    });

    return queries;
  }

  buildFilterParams() {
    // This is used to apply any filter that is not from a facet.
    // Basically, anything that does not come from a filter on the DOM.
    // This will probably not ever have anything other than the collection handle.
    const params = [];

    if (this.collectionHandle) {
      params.push(`collections:${this.collectionHandle}`);
    }

    return params.join(' AND ');
  }

  buildFacetParams() {
    // This is used to apply any filter that is from a facet.
    // A facet filter is a filter from the DOM.
    const params = [];

    Object.keys(this.currentFilters).forEach((facet) => {
      const facetGroup = [];

      this.currentFilters[facet].forEach((value) => {
        if (facet == 'price') {
          return;
        } else {
          facetGroup.push(`${facet}:${value}`);
        }
      });

      params.push(facetGroup);
    });

    return params;
  }

  buildNumericParams() {
    const params = [];

    Object.keys(this.currentFilters).forEach((facet) => {
      if (facet == 'price') {
        const min = this.currentFilters[facet][0] || this.priceResultsMin;
        const max = this.currentFilters[facet][1] || this.priceResultsMax;

        params.push(`price>=${min}`);
        params.push(`price<=${max}`);
      }
    });

    return params;
  }

  /**
   * Generate the sort options from the config.
   */
  generateSortFromConfig() {
    if (this.sortBy || this.sortByDrawer) {
      this.sortData.forEach((sort) => {
        var sortOption = false;

        if (sort.asc && sort.asc.active) {
          sortOption = sort.asc;
        }

        if (sort.desc && sort.desc.active) {
          sortOption = sort.desc;
        }

        if (sortOption) {
          const option = document.createElement('option');
          option.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          option.innerHTML = sortOption.title;

          const li = document.createElement('li');
          li.classList.add('filters__item', 'list-menu__item');

          const label = document.createElement('label');
          label.classList.add('filters__label', 'facets__label');
          label.htmlFor = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;

          const input = document.createElement('input');
          input.classList.add('mobile-facets__checkbox');
          input.type = 'radio';
          input.name = 'sort_by';
          input.value = `_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.id = `Filter_${sort.key}_${sort.asc ? 'asc' : 'desc'}`;
          input.checked = this.currentSortBy == option.value;

          const span = document.createElement('span');
          span.classList.add('facet-checkbox__text-label');
          span.innerHTML = sortOption.title;

          label.appendChild(input);
          label.appendChild(span);
          li.appendChild(label);

          if (this.currentSortBy == option.value) {
            option.selected = true;
          }

          if (this.sortBy) {
            this.sortBy.appendChild(option.cloneNode(true));

            if (this.currentSortBy == option.value) {
              this.sortBy.value = option.value;
            }
          }

          if (this.sortByDrawer) {
            this.sortByDrawer.appendChild(li.cloneNode(true));
          }
        }
      });
    }
  }

  /**
   * Generate the filters from the results.
   * @param {object} results - The results from the algoliaShopify API.
   * @param {object} facetLocation - The location of the facet in the results. We do this to maintain the order of the facets.
   */
  generateFiltersFromResults(results, facetLocation) {
    const openFilterTypes = this.getOpenFilterTypes();

    if (this.filtersDesktop) {
      this.filtersDesktop.innerHTML = '';
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.innerHTML = '';
    }

    this.facetData.forEach((facet, facetIndex) => {
      if (facet.enabled) {
        if (results[facetLocation[facet.name]]) {
          const facetValues =
            results[facetLocation[facet.name]].facets[facet.name];

          if (facetValues) {
            this.generateFilters(
              facet,
              facetValues,
              facetIndex,
              openFilterTypes
            );
          }
        } else {
          const facetValues = results[0].facets[facet.name];

          if (facetValues) {
            this.generateFilters(
              facet,
              facetValues,
              facetIndex,
              openFilterTypes
            );
          }
        }
      }
    });
  }

  /**
   * Generate the filters from the facets.
   * @param {object} facets - The facets from the algoliaShopify API.
   */
  generateFiltersFromFacets(facets) {
    const openFilterTypes = this.getOpenFilterTypes();

    if (this.filtersDesktop) {
      this.filtersDesktop.innerHTML = '';
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.innerHTML = '';
    }

    this.facetData.forEach((facet, facetIndex) => {
      if (facet.enabled) {
        if (facets[facet.name]) {
          this.generateFilters(
            facet,
            facets[facet.name],
            facetIndex,
            openFilterTypes
          );
        }
      }
    });
  }

  /**
   * Generate the filters from the facet values.
   * @param {object} facet - The facet from the algoliaShopify API.
   * @param {object} facetValues - The facet values from the algoliaShopify API.
   * @param {number} facetIndex - The index of the facet.
   * @param {array} openFilterTypes - The open filter types.
   */
  generateFilters(facet, facetValues, facetIndex, openFilterTypes) {
    const details = document.createElement('details');
    details.id = `Filter-${this.sectionId}-${facetIndex}`;
    details.classList.add('filters__details', 'border');
    details.dataset.index = `mobile-${this.currentPage}`;

    if (openFilterTypes.includes(details.id)) {
      details.open = true;
    }

    const facetTitleId = algoliaStringify(facet.title);

    const summary = document.createElement('summary');
    summary.classList.add('filters__summary', 'h3');
    summary.innerHTML = `<span>${facet.title}</span>`;
    details.appendChild(summary);

    const div = document.createElement('div');
    div.classList.add('mobile-facets__submenu');
    details.appendChild(div);    

    var facetKeys = Object.keys(facetValues);

    if(this.facetValuesSortOrder[facet.name]) {
      if(this.facetValuesSortOrder[facet.name].sortRemainingBy == "count") {
        facetKeys.sort((a, b) => {
          return facetValues[b] - facetValues[a];
        });
      }
      else if(this.facetValuesSortOrder[facet.name].sortRemainingBy == "alpha") {
        facetKeys.sort((a, b) => {
          return a.localeCompare(b);
        });
      }      

      if(this.facetValuesSortOrder[facet.name].order) { 
        facetKeys.sort((a, b) => {
          const ai = this.facetValuesSortOrder[facet.name].order.indexOf(a);
          const bi = this.facetValuesSortOrder[facet.name].order.indexOf(b);

          return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
        });        
        
        if(this.facetValuesSortOrder[facet.name].sortRemainingBy == "hidden") {
          facetKeys = facetKeys.filter((value) => {
            return this.facetValuesSortOrder[facet.name].order.includes(value);
          });
        }
      }

      if(this.facetValuesSortOrder[facet.name].hide) {
        facetKeys = facetKeys.filter((value) => {
          return !this.facetValuesSortOrder[facet.name].hide.includes(value);
        });
      }
    }

    if(facetKeys.length == 0) {
      return;
    }

    if (facet.name == 'price') {
      const min = this.currentFilters[facet.name]?.[0] || this.priceResultsMin;
      const max = this.currentFilters[facet.name]?.[1] || this.priceResultsMax;

      div.innerHTML = `
        <range-slider class="range-slider">
          <div class="range-slider__controls" aria-hidden="true">
            <div class="range-slider__control">
              <label for="AlgoliaPriceFromInput" class="visually-hidden">Min Price</label>
              <input 
                class="range-slider__control-input range-slider__control-input--from focus-inset"
                name='price_min'
                data-param='price'
                step="1"
                type="number"
                id="AlgoliaPriceFromInput"
                value="${Math.max(min, this.priceResultsMin)}"
                min="${this.priceResultsMin}"
                max="${this.priceResultsMax}"
              />
            </div>
            <div class="range-slider__control">
              <label for="AlgoliaPriceToInput" class="visually-hidden">Max Price</label>
              <input 
                class="range-slider__control-input range-slider__control-input--to"
                name='price_max'
                data-param='price'
                step="1"
                type="number"
                id="AlgoliaPriceToInput"
                value="${Math.min(max, this.priceResultsMax)}"
                min="${this.priceResultsMin}"
                max="${this.priceResultsMax}"
              />
            </div>
          </div>
          <div class="range-slider__inputs">
            <label for='AlgoliaPriceFromSlider' class='range-slider__marker range-slider__marker--from'>$${this.priceResultsMin}</label>
            <input 
              class='range-slider__input range-slider__input--from'
              name='price_min_slider'
              data-param='price'
              id="AlgoliaPriceFromSlider" 
              step="1"
              type="range" 
              value="${Math.max(min, this.priceResultsMin)}" 
              min="${this.priceResultsMin}" 
              max="${this.priceResultsMax}"
            />
            <label for='AlgoliaPriceToSlider' class='range-slider__marker range-slider__marker--to'>$${this.priceResultsMax}</label>
            <input 
              class='range-slider__input range-slider__input--to' 
              name='price_max_slider'
              data-param='price'
              id="AlgoliaPriceToSlider" 
              step="1"
              type="range" 
              value="${Math.min(max, this.priceResultsMax)}" 
              min="${this.priceResultsMin}"
              max="${this.priceResultsMax}"
            />            
          </div>          
        </range-slider>
      `;

    }
    else {
      const ul = document.createElement('ul');
      ul.classList.add('filters__list', 'list--unstyled');
      div.appendChild(ul);

      facetKeys.forEach((value, valueIndex) => {

        if(value == 'N/A') {
          return;
        }

        const li = document.createElement('li');
        li.classList.add('filters__item', 'list-menu__item');
        ul.appendChild(li);
  
        const label = document.createElement('label');
        label.classList.add('facets__label', 'filters__label');
        label.htmlFor = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
        li.appendChild(label);
  
        const input = document.createElement('input');
        input.classList.add('mobile-facets__checkbox');
        input.id = `Filter-${this.sectionId}-${facetIndex}-${valueIndex}`;
        input.type = 'checkbox';
        input.name = facet.name;
        input.value = value;
        input.checked =
          this.currentFilters[facet.name] &&
          this.currentFilters[facet.name].includes(value);
  
        const span = document.createElement('span');
        span.classList.add('facet-checkbox__text', 'facet-checkbox__text-label');
  
        const labelId = algoliaStringify(value);
  
        if (facet.name == 'options.colour' || facet.name == 'named_tags.Colour') {
          ul.classList.add('filters__list--grid');
  
          input.classList.add('visually-hidden');
          span.classList.add('visually-hidden');
  
          label.classList.add('filters__label--swatch');
          label.dataset.value = value.toLowerCase();
          label.setAttribute('title', value);       
  
          span.innerHTML = `${value}`;
        } else if (facet.name == 'options.size') {
          ul.classList.add('filters__list--grid');
  
          input.classList.add('visually-hidden');
  
          label.classList.add('filters__label--size');
          span.innerHTML = `${value}`; // TRANSLATIONS
        } else if (facet.name == 'price_range') {
          if (value == '0:10') {
            span.innerHTML = window.language.under_10;
          } else if (value.indexOf(':') > -1) {
            const priceRange = value.split(':');
            span.innerHTML = `$${priceRange[0]} - $${priceRange[1]}`;
          } else {
            span.innerHTML = `${value}`;
          }
        } else if (facet.name == 'named_tags.Discount Amount') {
          span.innerHTML = `${(parseFloat(value) * 100).toFixed(0)}%`;
        } else {
          span.innerHTML = `${value}`; // TRANSLATIONS
        }
  
        label.appendChild(input);
        label.appendChild(span);        
      });
    }    

    if (this.filtersDesktop) {
      const clone = details.cloneNode(true);

      clone.id = `${clone.id}-desktop`;

      clone.querySelectorAll('[id]').forEach((element) => {
        element.id = `${element.id}-desktop`;
      });

      clone.querySelectorAll('[for]').forEach((element) => {
        element.setAttribute('for', `${element.getAttribute('for')}-desktop`);
      });

      clone.setAttribute('open', '');

      this.filtersDesktop.appendChild(clone);

      const rangeSlider = clone.querySelector('range-slider');
      if (rangeSlider) {
        rangeSlider.load();
      }
    }

    if (this.filtersDrawer) {
      const clone = details.cloneNode(true);
      this.filtersDrawer.appendChild(clone);

      const rangeSlider = clone.querySelector('range-slider');
      if (rangeSlider) {
        rangeSlider.load();
      }
    }
  }

  /**
   * Generate the product cards from the hits.
   * @param {object} hits - The product hits from the algoliaShopify API.
   */
  generateProductsFromHits(hits, queryId) {
    const powerReviewProducts = [];

    hits.forEach((hit, index) => {
      const li = document.createElement('li');
      li.classList.add('grid__item');
      //li.dataset.cascade = true;
      //li.style.setProperty('--animation-order', `${index + this.indexAdjust}`);

      const product = document.createElement('card-component');
      product.classList.add('card', `color-${window.cards.color_scheme}`);

      product.dataset.productId = hit.id;
      product.dataset.objectId = hit.objectID;
      product.dataset.position = index + this.indexAdjust;
      product.dataset.queryId = queryId;

      // Map Algolia hit fields to Shopify Liquid equivalents
      product.dataset.id = hit.id; // data-id
      product.dataset.handle = hit.handle || ''; // data-handle
      product.dataset.url = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${hit.handle}`; // data-url
      product.dataset.title = hit.title || ''; // data-title
      product.dataset.vendor = hit.vendor || hit.brand || ''; // data-vendor
      product.dataset.type = hit.product_type || hit.type || ''; // data-type
      product.dataset.price =
        typeof hit.price === 'number' ? hit.price : parseFloat(hit.price) || 0; // data-price (no currency)

      const imageLink = document.createElement('a');
      imageLink.classList.add('card__link', 'link--unstyled');
      imageLink.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${hit.handle}`;
      imageLink.addEventListener('click', this.onLinkClick.bind(this));

      const imageWrapper = document.createElement('div');
      imageWrapper.classList.add('card__image-wrapper');

      let comingSoon = null;

      if (hit.meta?.custom?.coming_soon == "true") {
        const releaseAt = Date.parse(hit.meta?.custom?.coming_soon_date ?? '');

        if (Number.isNaN(releaseAt)) {
          comingSoon = { at: null };
        } else if (releaseAt > Date.now()) {
          comingSoon = { at: releaseAt };
        }
      }

      if (comingSoon) {
        const badge = document.createElement('div');
        badge.classList.add('badge', 'card__price-badge', 'badge--coming-soon');

        if (comingSoon.at) {
          product.dataset.availableAt = Math.floor(comingSoon.at / 1000);

          // Read back as a store wall-clock date so the badge matches the one
          // card.liquid renders, rather than shifting with the viewer. Padded
          // to two digits to match Liquid's %m.%d.
          const local = new Date(
            comingSoon.at - storeTimeZoneOffset(comingSoon.at)
          );
          const month = String(local.getUTCMonth() + 1).padStart(2, '0');
          const day = String(local.getUTCDate()).padStart(2, '0');

          badge.textContent = `${month}.${day}`;
        } else {
          badge.textContent = `${window.cards.coming_soon}`;
        }

        imageWrapper.appendChild(badge);
      }
      //else if (hit.compare_at_price > hit.price) {
      //  const badge = document.createElement('div');
      //  badge.classList.add('badge', 'card__price-badge', 'badge--percent-off');
      //  const percentOff =
      //    ((hit.compare_at_price - hit.price) / hit.compare_at_price) * 100;
      //  badge.innerHTML = `
      //    ${JDRounding(percentOff)}${window.cards.percent_off}
      //  `;
      //  imageWrapper.appendChild(badge);
      //}

      if (hit.meta?.custom?.custom_badges) {
        const customBadges = JSON.parse(hit.meta.custom.custom_badges);

        // Add the handleized custom badge to the class
        customBadges.forEach((customBadge, index) => {
          const customBadgeElement = document.createElement('div');
          customBadgeElement.classList.add(
            'badge',
            'card__price-badge',
            `badge--${index + 1}`,
            `badge--${handleize(customBadge)}`
          );
          customBadgeElement.innerHTML = customBadge;
          imageWrapper.appendChild(customBadgeElement);
        });
      }

      if (window.theme.wishlist_enabled && window.theme.card_wishlist_enabled && hit) {
        const wishlistContainer = document.createElement('div');
        wishlistContainer.classList.add('wishlist__button');

        const wishlistButton = document.createElement('button');
        wishlistButton.classList.add(
          'link',
          'swym__button',
          'product_' + hit.id
        );
        wishlistButton.dataset.withEpi = 'true';
        wishlistButton.dataset.swaction = 'addToWishlist';
        wishlistButton.dataset.productId = hit.id;
        wishlistButton.dataset.variantId = hit.objectID;
        wishlistButton.dataset.productUrl = `${location.origin}/products/${hit.handle}`;

        wishlistContainer.appendChild(wishlistButton);
        imageWrapper.appendChild(wishlistContainer);
      }

      const imageContainer = document.createElement('div');
      imageContainer.classList.add('card__image-container');

      let imageList = [];
      if (hit.named_tags?.Rollover_Img != '' && hit.named_tags.Rollover_Img != null) {
        imageList = [hit.image, hit.named_tags.Rollover_Img];
      } else {
        imageList = [hit.image];
      }

      const slider = document.createElement('ul');
      slider.classList.add(
        'slider',
        'grid',
        'grid--1-col',
        'slider--desktop',
        'slider--mobile'
      );
      slider.setAttribute('aria-label', 'Product Images');

      imageList.forEach((src) => {
        const li = document.createElement('li');
        li.classList.add('grid__item', 'slider__slide');

        const mediaContainer = document.createElement('div');
        mediaContainer.classList.add(
          'media',
          `media--${window.cards.aspect_ratio}`
        );

        const image = document.createElement('img');
        image.classList.add('motion-reduce');
        image.loading = 'lazy';
        image.width = 540;
        image.height = 540;
        image.src = src + '&width=540';
        image.srcset = `${src + '&width=180'} 180w, ${
          src + '&width=360'
        } 360w, ${src + '&width=540'} 540w, ${src + '&width=720'} 720w, ${
          src + '&width=900'
        } 900w, ${src + '&width=1080'} 1080w`;
        image.alt = hit.title;
        image.sizes =
          '(min-width: 750px) calc((100vw - 1rem) / 4), calc((100vw - 1rem) / 2)';

        mediaContainer.appendChild(image);
        li.appendChild(mediaContainer);
        slider.appendChild(li);
      });

      imageContainer.appendChild(slider);
      imageWrapper.appendChild(imageContainer);
      imageLink.appendChild(imageWrapper);

      const details = document.createElement('div');
      details.classList.add('card__details');      

      const title = document.createElement('div');
      title.classList.add('card__title');      

      const span = document.createElement('a');
      span.href = `${window.theme.locale == 'fr_CA' ? '/fr' : ''}/products/${
        hit.handle
      }`;
      span.classList.add('card__title-title');
      span.innerHTML = hit.title;

      const vendor = document.createElement('div');
      vendor.classList.add('card__subtitle', 'special');
      vendor.innerHTML = `${hit.vendor}`;

      title.appendChild(vendor);
      title.appendChild(span);
      details.appendChild(title);

      if (hit.meta?.custom?.subtitle) {
        const subtitle = document.createElement('div');
        subtitle.classList.add('card__subtitle', 'body');
        subtitle.innerHTML = hit.meta.custom.subtitle;
        details.appendChild(subtitle);
      }

      const priceContainer = document.createElement('div');
      priceContainer.classList.add('card__price');

      const price = document.createElement('div');
      price.classList.add('price');
      if (hit.compare_at_price > hit.price) {
        price.classList.add('price--on-sale');
      }

      price.innerHTML = `
        <div class="price__sale">
          <span class="visually-hidden visually-hidden--inline">Sale Price</span>
          <span class="price-item">
            $${hit.price} ${Shopify.currency.active}
          </span>
        </div>            

        ${
          hit.compare_at_price > hit.price
            ? `
          <div class="price__regular gray">
            <span class="visually-hidden visually-hidden--inline">Regular Price</span>
            <s class="price-item">
              $${hit.compare_at_price} ${Shopify.currency.active}
            </s>
          </div>
        `
            : ''
        }
      `;

      priceContainer.appendChild(price);
      details.appendChild(priceContainer);

      if (window.theme.reviews.enabled) {
        const reviews = document.createElement('div');
        reviews.classList.add('card__reviews');
        reviews.id = `CardReview-${hit.id}`;

        details.appendChild(reviews);
      }

      if (
        (window.cards.enable_quickadd ||
        window.cards.enable_quickadd_mobile) &&
        !comingSoon
      ) {
        let sizeType = false
        
        let limitQuantity = false;
        let limitQuantityValue = 0;

        for (const tag of hit.tags) {
          if (tag.indexOf('limit-quantity') > -1) {
            limitQuantity = true;
            limitQuantityValue = tag.split('limit-quantity-')[1];
            break;
          }

          if (tag.indexOf('Size Type_') > -1) {
            sizeType = tag.split('Size Type_')[1];
            break;
          }
        }

        const root = window.Shopify?.routes?.root || '';

        const quickaddTrigger = document.createElement('quickadd-container');
        quickaddTrigger.id = `QuickaddContainer-${hit.id}`;
        quickaddTrigger.dataset.productId = hit.id;
        quickaddTrigger.dataset.productHandle = hit.handle;
        quickaddTrigger.dataset.productUrl = `${root}products/${hit.handle}`;
        quickaddTrigger.classList.add('card__quickadd');

        if (!window.cards.enable_quickadd) {
          quickaddTrigger.classList.add('hidden--desktop');
        }

        if (!window.cards.enable_quickadd_mobile) {
          quickaddTrigger.classList.add('hidden--mobile');
        }        

        const quickAddProductForm = document.createElement('product-form');
        quickAddProductForm.classList.add('card__quickadd-form');
        quickAddProductForm.dataset.sectionId = 'Quickadd';
        quickAddProductForm.dataset.quickview = 'true';
        quickAddProductForm.dataset.productId = hit.id;
        quickAddProductForm.dataset.limitQuantity = limitQuantity;

        if (sizeType) {
          const quickAddHeader = document.createElement('div');
          quickAddHeader.classList.add('card__quickadd-header', 'hidden--desktop');
          quickAddHeader.innerHTML = sizeType;
          quickAddProductForm.appendChild(quickAddHeader);
        }

        const quickAddForm = document.createElement('form');
        quickAddForm.classList.add('quickadd__form');
        quickAddForm.id = `QuickaddForm-${hit.id}`;

        if (limitQuantity) {
          const limitQuantityInput = document.createElement('input');
          limitQuantityInput.type = 'hidden';
          limitQuantityInput.name = 'properties[_limitQuantity]';
          limitQuantityInput.value = 'true';
          quickAddForm.appendChild(limitQuantityInput);

          const noteInput = document.createElement('input');
          noteInput.type = 'hidden';
          noteInput.name = 'properties[Note]';
          noteInput.value = window.language.limit_quantity.replace('[quantity]', limitQuantityValue);
          quickAddForm.appendChild(noteInput);
        }

        const quickAddVariants = document.createElement('ul');
        quickAddVariants.classList.add('quickadd__variants');

        // hit.named_tags.Size and .In_Stock can be either arrays or single
        // strings (e.g. one-size products return "ONE" + a single in-stock
        // entry). Normalize both to arrays so the rest of the code is uniform.
        const sizes = Array.isArray(hit.named_tags.Size)
          ? hit.named_tags.Size
          : [hit.named_tags.Size];

        const inStock = Array.isArray(hit.named_tags.In_Stock)
          ? hit.named_tags.In_Stock
          : [hit.named_tags.In_Stock];

        const sizeOrder = (window.theme?.size_order_list || []).map((s) =>
          String(s).toLowerCase()
        );
        const sortedSizes = sizes.sort((a, b) => {
          const aIndex = sizeOrder.indexOf(String(a).toLowerCase());
          const bIndex = sizeOrder.indexOf(String(b).toLowerCase());
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });

        let singleSizeInStock = false;

        sortedSizes.forEach((size) => {
          const variantId = (() => {
            const match = inStock.find((entry) => {
              const [, afterPipe] = entry.split('|');
              const segments = afterPipe?.split(' / ').map((s) => s.trim()) || [];
              return segments.includes(String(size));
            });
            return match ? match.split('|')[0] : null;
          })();

          if (sizes.length == 1 && variantId) {
            singleSizeInStock = true;
          }

          const li = document.createElement('li');
          li.classList.add('quickadd__variant');

          const label = document.createElement('label');
          label.classList.add('quickadd__variant-label');
          label.for = `QuickaddVariant-${hit.id}-${size}`;
          label.innerHTML = `
            <span>${size}</span>
            <span class='hidden--desktop'>+</span>
          `;

          const input = document.createElement('input');
          input.id = `QuickaddVariant-${hit.id}-${size}`;
          input.type = 'radio';
          input.name = 'id';
          input.value = variantId || '';
          input.checked = singleSizeInStock ? true : false;
          input.disabled = !variantId;
          input.classList.add('quickadd__variant-checkbox', 'visually-hidden');
          
          input.dataset.optionName = 'Size';
          input.dataset.optionValue = size;
          input.dataset.productUrl = `${root}products/${hit.handle}`;
          input.dataset.sku = hit.sku;

          label.appendChild(input);
          li.appendChild(label);
          quickAddVariants.appendChild(li);
        });

        const quickaddButton = document.createElement('button');
        quickaddButton.id = `QuickaddButton-${hit.id}`;
        quickaddButton.type = 'submit';
        quickaddButton.disabled = singleSizeInStock ? false : true;
        quickaddButton.classList.add(
          'button',
          'button--secondary',
          'hidden--mobile',
          singleSizeInStock ? 'enabled' : 'disabled',
        );

        const quickaddSpan = document.createElement('span');

        if (singleSizeInStock) {
          quickaddSpan.textContent = window.variantStrings.addToCart;
        } else if (sizes.length == 1 && !singleSizeInStock) {
          quickaddSpan.textContent = window.variantStrings.soldOut;
        } else {
          quickaddSpan.textContent = window.variantStrings.selectSize;
        }

        quickaddButton.appendChild(quickaddSpan);

        quickAddForm.appendChild(quickAddVariants);
        quickAddForm.appendChild(quickaddButton);

        quickAddProductForm.appendChild(quickAddForm);
        
        quickaddTrigger.appendChild(quickAddProductForm);

        const quickaddToggle = document.createElement('quickadd-trigger');
        quickaddToggle.classList.add('card__quickadd-trigger', 'hidden--desktop');
        quickaddToggle.innerHTML = `
          <button class='button button--secondary button--small'>
            ${ window.variantStrings.buyNow }
            <svg
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
              class="icon icon-arrow"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path d="M3.5 7.5H13.5C13.6326 7.5 13.7598 7.55268 13.8536 7.64645C13.9473 7.74021 14 7.86739 14 8C14 8.13261 13.9473 8.25979 13.8536 8.35355C13.7598 8.44732 13.6326 8.5 13.5 8.5H3.5C3.36739 8.5 3.24021 8.44732 3.14645 8.35355C3.05268 8.25979 3 8.13261 3 8C3 7.86739 3.05268 7.74021 3.14645 7.64645C3.24021 7.55268 3.36739 7.5 3.5 7.5Z" fill="currentColor"/>
              <path d="M3.70692 8.00001L7.85392 12.146C7.9478 12.2399 8.00055 12.3672 8.00055 12.5C8.00055 12.6328 7.9478 12.7601 7.85392 12.854C7.76003 12.9479 7.63269 13.0006 7.49992 13.0006C7.36714 13.0006 7.2398 12.9479 7.14592 12.854L2.64592 8.35401C2.59935 8.30756 2.56241 8.25238 2.5372 8.19164C2.512 8.13089 2.49902 8.06577 2.49902 8.00001C2.49902 7.93424 2.512 7.86912 2.5372 7.80837C2.56241 7.74763 2.59935 7.69245 2.64592 7.64601L7.14592 3.14601C7.2398 3.05212 7.36714 2.99937 7.49992 2.99937C7.63269 2.99937 7.76003 3.05212 7.85392 3.14601C7.9478 3.23989 8.00055 3.36723 8.00055 3.50001C8.00055 3.63278 7.9478 3.76012 7.85392 3.85401L3.70692 8.00001Z" fill="currentColor"/>
            </svg>
          </button>
        `;
        quickaddTrigger.appendChild(quickaddToggle);

        details.appendChild(quickaddTrigger);

        quickAddProductForm.init();
      }

      product.appendChild(imageLink);
      product.appendChild(details);      

      li.appendChild(product);
      this.productList.appendChild(li);

      const pageTitle = (document.title || "").split(/\s[-–—]\s/)[0].trim();

      if (this.collectionPromos) {
        const promos = Shopify.locale == "fr" ? this.collectionPromos.tiles_fr : this.collectionPromos.tiles;
        const bannerData = Shopify.locale == "fr" ? this.collectionPromos.banner_fr : this.collectionPromos.banner;

        if(promos.length > 0) {
          promos.forEach((tile, tileIndex) => {
            if (index + this.indexAdjust == tile.position && tile.image) {
              const tileLi = document.createElement('li');
              tileLi.classList.add('grid__item');

              const tileMedia = document.createElement('div');
              tileMedia.classList.add('media', 'media--collection');

              if (tile.link == '' && tile.name != '') {
                tileMedia.dataset.promoTileName = tile.name;
              }

              const tileImage = document.createElement('img');
              tileImage.classList.add('motion-reduce');
              tileImage.loading = 'lazy';
              tileImage.width = tile.width;
              tileImage.height = tile.height;
              tileImage.src = tile.image;
              tileImage.srcset = `${tile.image + '&width=180'} 180w, ${
                tile.image + '&width=360'
              } 360w, ${tile.image + '&width=540'} 540w, ${
                tile.image + '&width=720'
              } 720w, ${tile.image + '&width=900'} 900w, ${
                tile.image + '&width=1080'
              } 1080w`;
              tileImage.sizes =
                '(min-width: 750px) calc((100vw - 1rem) / 4), calc((100vw - 1rem) / 2)';
              tileImage.alt = tile.title;

              tileMedia.appendChild(tileImage);

              const tileLink = document.createElement('a');
              tileLink.classList.add('media', 'media--collection');
              tileLink.href = tile.link;
              tileLink.ariaLabel = tile.title;
              if (tile.name != '') {
                tileLink.dataset.promoTileName = tile.name;
                tileLink.dataset.internalPromotionName = 'Promo Card - ' + tile.name;
                tileLink.dataset.internalPromotionId = 'Promo Card - ' + tile.name;
                tileLink.dataset.internalCreativeName = tile.image;
                tileLink.dataset.internalCreativeSlot = pageTitle + ' - Promo Card';
              }

              if (tile.link != '') {
                tileLink.appendChild(tileMedia);
                tileLi.appendChild(tileLink);
              } else {
                tileLi.appendChild(tileMedia);
              }

              this.productList.appendChild(tileLi);
              this.indexAdjust++;
            }
          });
        }

        if (
          index + this.indexAdjust == 7 &&
          bannerData.image
        ) {
          const banner = document.createElement('div');
          banner.classList.add('listing__banner');
          banner.innerHTML = `
            <a href="${bannerData.link}"
              class="link link--unstyled listing__banner-link" ${
                      bannerData.promo_banner_name
                        ? `
              data-internal-creative-name="${bannerData.image}"
              data-internal-creative-slot="${pageTitle} - Mid Banner"
              data-internal-promotion-id="Mid Banner - ${bannerData.promo_banner_name}"
              data-internal-promotion-name="Mid Banner - ${bannerData.promo_banner_name}"` : ""
            }>
            <div class='media media--adapt' style='padding-bottom: ${
              (bannerData.height /
                bannerData.width) *
              100
            }%;'>
              <img src="${bannerData.image}" alt="${
            bannerData.title
          }" class="listing__banner-image">
            </div>
          </a>
        `;

          this.productList.appendChild(banner);
        }
      }

      powerReviewProducts.push({
        api_key: window.theme.reviews.api_key,
        locale: window.theme.reviews.locale,
        merchant_group_id: window.theme.reviews.group_id,
        merchant_id: window.theme.reviews.merchant_id,
        page_id: hit.id,
        components: {
          CategorySnippet: `CardReview-${hit.id}`,
        },
      });
    });

    this.indexAdjust += hits.length;

    if (window.theme.wishlist_enabled && window.theme.card_wishlist_enabled) {
      document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    }

    if (window.theme.reviews.enabled) {
      setTimeout(() => {
        pwr('render', powerReviewProducts);
      }, 150);
    }
  }

  generateCountsFromResults(results) {
    this.itemCounts.forEach((count) => {
      count.innerHTML = `(${results.nbHits} ${window.paginationStrings.items})`;
    });
  }

  /**
   * Generate the pagination from the results.
   * @param {object} results - The results from the algoliaShopify API.
   */
  generatePaginationFromResults(results) {
    if (results.nbPages == 0) {
      this.paginationContainer.remove();
      return;
    }

    /*
    this.paginationContainer.dataset.hasMore =
      results.page + 1 != results.nbPages;

    this.paginationContainer.style.setProperty(
      '--fill',
      `${
        ((results.page * results.hitsPerPage + results.hits.length) /
          results.nbHits) *
        100
      }%`
    );
    */

    const numbersContainer = this.paginationContainer.querySelector('.pagination__numbers');
    //const ofText = this.paginationContainer.querySelector('.pagination__of');
    //const select = this.paginationContainer.querySelector(
    //  '.pagination__select'
    //);
    const prevButton = this.paginationContainer.querySelector(
      '.pagination__button--prev'
    );
    const nextButton = this.paginationContainer.querySelector(
      '.pagination__button--next'
    );

    if (results.page == 0) {
      prevButton.disabled = true;
    } else {
      prevButton.disabled = false;
    }

    if (results.page == results.nbPages - 1) {
      nextButton.disabled = true;
    } else {
      nextButton.disabled = false;
    }

    numbersContainer.innerHTML = '';
    const paginationNumbers = this.generatePaginationNumbers((results.page + 1), results.nbPages);
    
    paginationNumbers.forEach((number) => {
      if(number == '...') {
        const span = document.createElement('span');
        span.classList.add('pagination__number', 'pagination__number--ellipsis');
        span.innerHTML = number;
        numbersContainer.appendChild(span);
        return;
      }

      const button = document.createElement('button');
      button.classList.add('pagination__number');

      if(number == (results.page + 1)) {
        button.classList.add('pagination__number--current');
        button.disabled = true;
      }

      button.innerHTML = number;
      button.dataset.page = number - 1;
      button.addEventListener('click', this.onPaginationClick.bind(this));
      numbersContainer.appendChild(button);
    });
    

    //ofText.innerHTML = window.paginationStrings.of.replace(
    //  '{{ number }}',
    //  results.nbPages
    //);

    // Populate the select with options for each page
    //select.innerHTML = '';
    //for (let i = 0; i < results.nbPages; i++) {
    //  const option = document.createElement('option');
    //  option.value = i;
    //  option.innerHTML = i + 1;
    //  select.appendChild(option);
    //
    //  if (i == results.page) {
    //    option.selected = true;
    //  }
    //}

    /*
    this.paginationContainer.querySelector('.pagination__text').innerHTML =
      window.paginationStrings.viewed
        .replace(
          '{{ number }}',
          results.page * results.hitsPerPage + results.hits.length
        )
        .replace('{{ total }}', results.nbHits);

    if (results.page != results.nbPages) {
      this.paginationContainer.querySelector(
        '.pagination__more button'
      ).innerHTML = window.paginationStrings.show_more.replace(
        '{{ number }}',
        results.hitsPerPage
      );
    }
    */
  }

  generatePaginationNumbers(currentPage, totalPages) {
      // Edge case: only one page
    if (totalPages <= 1) return [1];

    const pages = new Set();

    // Rule 1 & 2: always show first and last
    pages.add(1);
    pages.add(totalPages);

    // Rules 3, 4, 5: add pages around current
    if (currentPage === 1) {
      // First page: show 1, 2, 3, ..., lp
      pages.add(2);
      pages.add(3);
    } else if (currentPage === totalPages) {
      // Last page: show 1, ..., lp-2, lp-1, lp
      pages.add(totalPages - 2);
      pages.add(totalPages - 1);
    } else {
      // Middle page: show 1, ..., cp-1, cp, cp+1, ..., lp
      pages.add(currentPage - 1);
      pages.add(currentPage);
      pages.add(currentPage + 1);
    }

    // Sort and filter to valid range
    const sorted = [...pages]
      .filter(p => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);

    // Insert ellipsis where there are gaps, but only if gap > 1 page (Rule 6)
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0) {
        const gap = sorted[i] - sorted[i - 1];
        if (gap === 2) {
          // Gap of exactly one page: show that page instead of "..."
          result.push(sorted[i - 1] + 1);
        } else if (gap > 2) {
          result.push('...');
        }
      }
      result.push(sorted[i]);
    }    

    return result;
  }

  generatePillsFromFacets() {
    let hasFilters = false;

    this.pillsContainer.innerHTML = '';
    this.cleanEmptyFilters();

    Object.keys(this.currentFilters).forEach((filterGroup) => {
      this.currentFilters[filterGroup].forEach((filter, filterIndex) => {
        if (filterGroup == 'price' && filterIndex > 0) {
          return;
        }

        const pill = document.createElement('button');
        pill.classList.add('filters__pill');
        pill.dataset.filterGroup = filterGroup;
        pill.dataset.filter = filter;

        if (filterGroup == 'price') {
          pill.textContent = `
            $${this.currentFilters[filterGroup][0]} - $${this.currentFilters[filterGroup][1]}
          `;
        }
        else if (filterGroup == 'price_range') {
          pill.textContent = `$${filter.split(':')[0]} - $${
            filter.split(':')[1]
          }`;
        } else {
          pill.textContent = `${filter}`;
        }

        const span = document.createElement('span');
        span.classList.add('visually-hidden');
        span.textContent = `Remove filter: ${filter}`;

        pill.appendChild(span);

        pill.addEventListener('click', this.onFilterPillClick.bind(this));

        this.pillsContainer.appendChild(pill);

        hasFilters = true;
      });
    });

    if (hasFilters) {
      const pill = document.createElement('a');
      pill.classList.add('link', 'link--underlined', 'filters__pill--clear');
      pill.href = `${window.location.pathname}`;
      pill.innerHTML = window.language.clear_all;

      const span = document.createElement('span');
      span.classList.add('visually-hidden');
      span.innerHTML = `Remove all filters`;

      pill.appendChild(span);

      this.pillsContainer.appendChild(pill);
    }
  }

  /**
   * Get the open filter types. This is used to maintain the open/closed state of the filters.
   * @returns {array} The open filter types.
   */
  getOpenFilterTypes() {
    const openFiltersDesktop = [];
    const openFiltersDrawer = [];

    if (this.filtersDesktop) {
      this.filtersDesktop.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDesktop.push(details.id);
        }
      });
    }

    if (this.filtersDrawer) {
      this.filtersDrawer.querySelectorAll('details').forEach((details) => {
        if (details.open) {
          openFiltersDrawer.push(details.id);
        }
      });
    }

    return openFiltersDesktop.concat(openFiltersDrawer);
  }

  cleanEmptyFilters() {
    Object.keys(this.currentFilters).forEach((key) => {
      if (
        !this.currentFilters[key] ||
        this.currentFilters[key].length === 0
      ) {
        delete this.currentFilters[key];
      }
    });
  }

  onFilterPillClick(event) {
    const target = event.target.closest("[data-filter-group]");

    if (!target) return;

    const filterGroup = target.dataset.filterGroup;
    const filter = target.dataset.filter;

    const input = this.querySelector(
      `input[name="${filterGroup}"][value="${CSS.escape(filter)}"]`,
    );

    if (filterGroup == 'price') {
      const priceMinSlider = this.querySelector(`input[name="price_min_slider"]`);
      const priceFromInput = this.querySelector(`input[name="price_min"]`);

      const priceMaxSlider = this.querySelector(`input[name="price_max_slider"]`);
      const priceToInput = this.querySelector(`input[name="price_max"]`);

      priceMinSlider.value = priceMinSlider.min;
      priceFromInput.value = priceMinSlider.min;
      priceMaxSlider.value = priceMaxSlider.max;
      priceToInput.value = priceMaxSlider.max;

      this.onFilterChange(priceMinSlider);
    }
    else if (input) {
      const input = this.querySelector(
        `input[name="${filterGroup}"][value="${filter}"]`
      );
      input.checked = false;

      this.onFilterChange(input);
      return;
    }

    if (this.currentFilters[filterGroup]) {
      this.currentFilters[filterGroup] = this.currentFilters[
        filterGroup
      ].filter((v) => v !== filter);

      if (this.currentFilters[filterGroup].length === 0) {
        delete this.currentFilters[filterGroup];
      }
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;

    const values = params.getAll(filterGroup);

    if (values.length) {
      const newValues = values.filter((v) => v !== filter);

      params.delete(filterGroup);
      newValues.forEach((v) => params.append(filterGroup, v));
    }

    window.history.replaceState({}, "", url.pathname + "?" + params.toString());

    this.search();
  }

  /**
   * Handle the filter change.
   * @param {object} event - The event object.
   */
  onFilterChange(event) {
    var target = event;

    if (event.target) {
      target = event.target;
    }

    const details = target.closest('details');
    const form = target.closest('form');

    const name = target.dataset.param || target.name;
    const value = target.value;

    if (!this.currentFilters[name]) {
      this.currentFilters[name] = [];
    }

    if (name == 'price') {
      let min = value;
      let max = value;

      details.querySelectorAll('input[type="number"]').forEach((input) => {
        if (input.name == 'price_min') {
          min = parseInt(input.value);
        } else if (input.name == 'price_max') {
          max = parseInt(input.value);
        }
      });

      if (this.priceResultsMin != min || this.priceResultsMax != max) {
        this.currentFilters[name] = [min, max];
      } else {
        delete this.currentFilters[name];
      }
    }
    else if (target.type == 'checkbox') {
      if (target.checked) {
        this.currentFilters[name].push(value);

        if (window.theme.dynamic_yield_enabled && DynamicYield) {
          DynamicYield.filterItems(target.name, target.value);

          if (aa) {
            aa('clickedFilters', {
              userToken: getAlgoliaUserToken(),
              index: this.buildIndexParams(),
              eventName: 'PLP: Filter Clicked',
              filters: [`${target.name}:${target.value}`],
            });
          }
        }
      } else {
        this.currentFilters[name] = this.currentFilters[name].filter(
          (item) => item !== value
        );
      }
    }

    if (form == this.filtersDesktop) {
      const siblingInput = this.filtersDrawer.querySelector(
        `input[name="${target.name}"][value="${target.value}"]`
      );

      if (siblingInput) {
        siblingInput.checked = target.checked;
      }
    }

    if (this.filtersDesktop && form == this.filtersDrawer) {
      const siblingInput = this.filtersDesktop.querySelector(
        `input[name="${target.name}"][value="${target.value}"]`
      );

      if (siblingInput) {
        siblingInput.checked = target.checked;
      }
    }

    this.updateURLHash();
    this.search();
  }

  /**
   * Handle the sort change.
   * @param {object} event - The event object.
   */
  onSortChange(event) {
    this.currentSortBy = event.target.value;

    if (event.target.id == 'AlgoliaDrawerSortBy') {
      this.sortBy.value = event.target.value;
    } else {
      this.sortByDrawer.querySelectorAll('input').forEach((input) => {
        if (input.value == event.target.value) {
          input.checked = true;
        } else {
          input.checked = false;
        }
      });
    }

    if (window.theme.dynamic_yield_enabled && DynamicYield) {
      const order =
        event.target.value.split('_').pop() === 'asc' ? 'asc' : 'desc';
      DynamicYield.sortItems(event.target.value, order);
    }

    this.updateURLHash();
    this.search();
  }

  /**
   * Handle the pagination events.
   * @param {object} event - The event object.
   */
  onPaginationClick(event) {
    this.currentPage = parseInt(event.target.dataset.page);

    this.updateURLHash();

    this.search(false, true);
  }

  onPaginationSelect(event) {
    this.currentPage = event.target.value;

    this.updateURLHash();

    this.search(false, true);
  }

  onPaginationPrev(event) {
    this.currentPage--;

    this.updateURLHash();

    this.search(false, true);
  }

  onPaginationNext(event) {
    this.currentPage++;

    this.updateURLHash();

    this.search(false, true);
  }

  onLinkClick(event) {
    const card = event.target.closest('.card');

    const position = parseInt(card.dataset.position) + 1;
    
    aa('clickedObjectIDsAfterSearch', {
      userToken: getAlgoliaUserToken(),
      eventName: 'PLP: Search Result Clicked',
      index: this.buildIndexParams(),
      queryID: card.dataset.queryId,
      objectIDs: [card.dataset.objectId],
      positions: [position],
    });

    addToAlgoliaStorage(card.dataset.productId, card.dataset.queryId, this.buildIndexParams());
  }

  /**
   * Update the URL hash.
   */
  updateURLHash() {
    const params = new URLSearchParams();

    if (this.searchTerms) {
      params.set('q', this.searchTerms);
    }
    
    this.cleanEmptyFilters();

    Object.keys(this.currentFilters).forEach((filter) => {
      this.currentFilters[filter].forEach((value) => {
        params.append(filter, value);
      });
    });

    if (this.currentSortBy) {
      params.set('sort_by', this.currentSortBy);
    }

    if (this.currentPage !== 0) {
      params.set('page', this.currentPage);
    }

    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    history.pushState(
      {
        page: this.currentPage,
        searchParams: this.currentFilters,
        sortBy: this.currentSortBy,
      },
      '',
      newUrl
    );
  }
}

customElements.define('listing-algolia', ListingAlgolia);

class ListingComponent extends HTMLElement {
  constructor() {
    super();

    this.filterIds = ['FiltersForm', 'FiltersFormDrawer'];

    this.template = this.dataset.template;
    this.sectionId = this.dataset.sectionId;
    this.cache = [];

    this.initialSearchParams = window.location.search.slice(1);
    this.currentSearchParams = window.location.search.slice(1);

    this.hasFilters = false;

    this.forms = this.querySelectorAll('listing-form form');

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.forms.forEach((form) => {
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));

      if (this.filterIds.indexOf(form.id) > -1) {
        this.hasFilters = true;
      }
    });

    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : this.initialSearchParams;

      if (searchParams === this.currentSearchParams) return;

      this.renderPage(searchParams, null, false);
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const forms = [];

    this.forms.forEach((form) => {
      if (this.filterIds.indexOf(form.id) > -1) {
        if (form.contains(event.target)) {
          forms.push(this.createSearchParams(form));
        }
      } else {
        forms.push(this.createSearchParams(form));
      }
    });

    this.renderPage(forms.join('&'), event);
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    const priceRangeEntries = Array.from(formData.entries()).filter(([key]) =>
      key.startsWith('price_range')
    );

    if (priceRangeEntries.length > 0) {
      var priceMin = 99999999;
      var priceMax = 0;

      priceRangeEntries.forEach(([key, value]) => {
        const [min, max] = value.split('-');

        if (min < priceMin) {
          priceMin = min;
        }

        if (max > priceMax) {
          priceMax = max;
        }
      });

      formData.append(`filter.v.price.gte`, (priceMin / 100).toFixed(2));

      formData.append(`filter.v.price.lte`, (priceMax / 100).toFixed(2));

      formData.delete('price_range');
    }

    return new URLSearchParams(formData).toString();
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.currentSearchParams = searchParams;

    this.classList.add('loading');

    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;

    this.cache.some((element) => element.url === url)
      ? this.renderSectionFromCache(url, event)
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        this.buildPage(url, html, event);
      });
  }

  renderSectionFromCache(url, event) {
    const html = this.cache.find((element) => element.url === url).html;

    this.buildPage(url, html, event);
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  buildPage(url, html, event) {
    this.cache.push({ html, url });

    if (this.hasFilters) {
      this.buildFilters(html, event);
    }

    this.buildProducts(html, event);

    this.classList.remove('loading');
  }

  buildFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    this.filterIds.forEach((filterId) => {
      const filterFromFetch = parsedHTML.getElementById(filterId);
      const filterFromDom = document.getElementById(filterId);

      if (!filterFromFetch) return;
      if (!filterFromDom) return;

      const detailsFromFetch = parsedHTML.querySelectorAll(
        `#${filterId} details`
      );
      const detailsFromDom = document.querySelectorAll(`#${filterId} details`);

      var openDetails = [];
      var closedDetails = [];

      detailsFromDom.forEach((detail) => {
        if (detail.open) {
          openDetails.push(detail.id);
        } else {
          closedDetails.push(detail.id);
        }
      });

      detailsFromFetch.forEach((detail) => {
        if (openDetails.indexOf(detail.id) > -1) {
          detail.open = true;
        } else {
          detail.open = false;
        }
      });

      filterFromDom.innerHTML = filterFromFetch.innerHTML;
    });

    if (event) {
      const selectedInput = document.querySelector(`#${event.target.id}`);

      if (selectedInput) {
        selectedInput.focus();
      }
    }
  }

  buildProducts(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    document.getElementById('ProductGridContainer').innerHTML =
      parsedHTML.getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.remove('scroll-trigger');
        element.classList.remove('animate--slide-in');
      });
  }
}

customElements.define('listing-component', ListingComponent);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class LoadMore extends HTMLElement {
  constructor() {
    super();

    this.params = this.dataset.params;

    this.listing = this.closest('listing-component');

    this.button = this.querySelector('.pagination__more');

    this.button.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick() {
    this.listing.renderPage(this.params, null, false);
  }
}

customElements.define('load-more', LoadMore);



class FiltersDesktopToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.expanded = false;
  }

  connectedCallback() {
    if (!this.button) return;

    this.asides = document.querySelectorAll('.filters__desktop');

    // ARIA disclosure-pattern wiring. aria-expanded communicates the state
    // to screen readers; aria-controls points at the elements being
    // disclosed so assistive tech can announce the relationship.
    this.button.setAttribute('aria-expanded', 'false');
    const ids = [];
    this.asides.forEach((aside, index) => {
      if (!aside.id) aside.id = `FiltersDesktopAside-${index}`;
      ids.push(aside.id);
      // `inert` removes the subtree from the focus order and from the
      // accessibility tree so that keyboard / screen reader users can't land
      // inside the collapsed filters by accident.
      aside.inert = true;
    });
    if (ids.length) this.button.setAttribute('aria-controls', ids.join(' '));

    this.button.addEventListener('click', this.toggle.bind(this));
  }

  toggle() {
    this.expanded = !this.expanded;
    this.button.setAttribute('aria-expanded', String(this.expanded));
    this.asides.forEach((aside) => {
      aside.classList.toggle('filters__desktop--open', this.expanded);
      aside.inert = !this.expanded;
    });
  }
}

customElements.define('filters-desktop-toggle', FiltersDesktopToggle);
